import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Card } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useWorkspaces } from '@/hooks/useWorkspaces';
import { supabase } from '@/lib/supabase';
import { XCircle, ChevronLeft, ChevronRight } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';
import { captureError } from '@/lib/sentry';

interface EmailJob {
  id: string;
  scheduled_send_time: string;
  recipient_email: string;
  rendered_subject: string;
  provider_email: string;
  provider_type: string;
  status: string;
  campaign_id: string | null;
  campaign_name: string;
  daily_limit: number;
}

interface DayWithJobs {
  date: DateTime;
  jobs: EmailJob[];
  capacity: number;
  sent: number;
  isCurrentMonth: boolean;
  isToday: boolean;
}

const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const getProviderColor = (email: string) => {
  const colors = [
    'bg-blue-100 text-blue-800',
    'bg-green-100 text-green-800',
    'bg-purple-100 text-purple-800',
    'bg-orange-100 text-orange-800',
    'bg-pink-100 text-pink-800',
  ];
  
  const hash = email.split('').reduce((acc, char) => {
    return char.charCodeAt(0) + ((acc << 5) - acc);
  }, 0);
  
  return colors[Math.abs(hash) % colors.length];
};

export function ScheduledEmailsCalendar() {
  const [currentDate, setCurrentDate] = useState(DateTime.now());
  const [selectedDate, setSelectedDate] = useState<DateTime>(DateTime.now());
  const [selectedProviders, setSelectedProviders] = useState<string[]>([]);
  const [selectedCampaigns, setSelectedCampaigns] = useState<string[]>([]);
  const startOfToday = DateTime.now().startOf('day');
  const { currentWorkspace } = useWorkspaces();
  const { toast } = useToast();

  // Fetch email jobs for the current month view
  const { data: emailJobs = [] } = useQuery({
    queryKey: ['scheduledEmails', currentWorkspace?.id, currentDate.toISO()],
    queryFn: async () => {
      if (!currentWorkspace?.id) return [];

      const startOfView = currentDate.startOf('month').minus({ days: 7 });
      const endOfView = currentDate.endOf('month').plus({ days: 7 });

      const { data, error } = await supabase
        .from('hermes_email_queue_jobs')
        .select(`
          id,
          scheduled_send_time,
          recipient_email,
          rendered_subject,
          status,
          campaign_id,
          hermes_email_accounts!inner (
            email_address,
            provider,
            daily_email_limit
          ),
          hermes_email_campaigns (
            name
          )
        `)
        .eq('workspace_id', currentWorkspace.id)
        .gte('scheduled_send_time', startOfView.toISO())
        .lte('scheduled_send_time', endOfView.toISO())
        // status CHECK only permits pending/processing/sent/failed/retrying — 'scheduled' never existed; pending-only is what this view meant.
        .eq('status', 'pending')
        .order('scheduled_send_time', { ascending: true });

      if (error) throw error;

      return (data || []).map(job => {
        // PostgREST nested selects aren't in the generated row types — read both nested shapes off one typed cast.
        const row = job as unknown as {
          campaign_id?: string | null;
          hermes_email_accounts?: { email_address?: string; provider?: string; daily_email_limit?: number | null };
          hermes_email_campaigns?: { name?: string | null };
        };
        return {
          id: job.id,
          scheduled_send_time: job.scheduled_send_time,
          recipient_email: job.recipient_email,
          rendered_subject: job.rendered_subject,
          provider_email: row.hermes_email_accounts?.email_address || '',
          provider_type: row.hermes_email_accounts?.provider || '',
          status: job.status,
          campaign_id: row.campaign_id ?? null,
          campaign_name: row.hermes_email_campaigns?.name ?? '',
          daily_limit: row.hermes_email_accounts?.daily_email_limit ?? 0,
        };
      });
    },
    enabled: !!currentWorkspace?.id
  });

  // Get unique providers from jobs
  const providers = useMemo(() => {
    const uniqueProviders = new Set(emailJobs.map(job => job.provider_email));
    return Array.from(uniqueProviders);
  }, [emailJobs]);

  // Get unique campaigns from jobs (fall back to a short-id label when name is missing)
  const campaigns = useMemo(() => {
    const byId = new Map<string, { id: string; name: string }>();
    for (const job of emailJobs) {
      if (!job.campaign_id || byId.has(job.campaign_id)) continue;
      byId.set(job.campaign_id, {
        id: job.campaign_id,
        name: job.campaign_name || `Campaign ${job.campaign_id.slice(0, 8)}`,
      });
    }
    return Array.from(byId.values());
  }, [emailJobs]);

  // Actual deliveries per day (the truth of what went out), same view range.
  // Keeps campaign/provider identity so the delivered badges honor the filters.
  const { data: sentEmails = [] } = useQuery({
    queryKey: ['sentEmailsByDay', currentWorkspace?.id, currentDate.toISO()],
    queryFn: async () => {
      if (!currentWorkspace?.id) return [];
      const startOfView = currentDate.startOf('month').minus({ days: 7 });
      const endOfView = currentDate.endOf('month').plus({ days: 7 });
      const { data, error } = await supabase
        .from('sent_emails')
        .select('sent_at, campaign_id, sender_email')
        .eq('workspace_id', currentWorkspace.id)
        .gte('sent_at', startOfView.toISO())
        .lte('sent_at', endOfView.toISO());
      if (error) throw error;
      return (data || []).flatMap(r =>
        r.sent_at
          ? [{
              day: DateTime.fromISO(r.sent_at).toLocal().startOf('day'),
              campaign_id: r.campaign_id,
              sender_email: r.sender_email,
            }]
          : [],
      );
    },
    enabled: !!currentWorkspace?.id,
  });

  // Delivered per day keyed by ISO date — respects the active campaign/provider filters.
  const sentCountByDay = useMemo(() => {
    const camp = new Set(selectedCampaigns);
    const prov = new Set(selectedProviders);
    const m = new Map<string, number>();
    for (const e of sentEmails) {
      if (camp.size && (!e.campaign_id || !camp.has(e.campaign_id))) continue;
      if (prov.size && (!e.sender_email || !prov.has(e.sender_email))) continue;
      const k = e.day.toISODate() ?? '';
      m.set(k, (m.get(k) ?? 0) + 1);
    }
    return m;
  }, [sentEmails, selectedCampaigns, selectedProviders]);

  // Jobs that survive the provider/campaign filters — used for the day cells,
  // the overdue banner, and the today roll-up, so every number matches the view.
  const filteredJobs = useMemo(
    () => emailJobs.filter(job =>
      (selectedProviders.length === 0 || selectedProviders.includes(job.provider_email)) &&
      (selectedCampaigns.length === 0 || (job.campaign_id !== null && selectedCampaigns.includes(job.campaign_id)))),
    [emailJobs, selectedProviders, selectedCampaigns],
  );

  // Pending jobs whose original schedule is before today — the backlog that
  // did not go out on their slated day; they roll forward, they are not errors.
  const overdueCount = useMemo(
    () => filteredJobs.filter(j => DateTime.fromISO(j.scheduled_send_time).startOf('day') < startOfToday).length,
    [filteredJobs, startOfToday],
  );

  // Jobs eligible to send TODAY (past-due backlog rolls forward + today's schedule):
  // claims are FIFO by scheduled time, so a fully-behind campaign still shows its
  // activity here instead of vanishing from the calendar.
  const todayEligible = useMemo(
    () => filteredJobs.filter(j => DateTime.fromISO(j.scheduled_send_time).startOf('day') <= startOfToday).length,
    [filteredJobs, startOfToday],
  );

  // Generate calendar days
  const calendarDays = useMemo(() => {
    const days: DayWithJobs[] = [];
    const startOfMonth = currentDate.startOf('month');
    const startOfCalendar = startOfMonth.startOf('week');
    const endOfMonth = currentDate.endOf('month');
    const endOfCalendar = endOfMonth.endOf('week');

    let currentDay = startOfCalendar;
    while (currentDay <= endOfCalendar) {
      const dayJobs = filteredJobs.filter(job => {
        const jobDate = DateTime.fromISO(job.scheduled_send_time);
        return jobDate.hasSame(currentDay, 'day');
      });

      // Capacity = sum of each unique account's daily_email_limit over the jobs visible this day
      const accountLimits = new Map<string, number>();
      for (const job of dayJobs) {
        if (!accountLimits.has(job.provider_email)) {
          accountLimits.set(job.provider_email, job.daily_limit);
        }
      }
      const capacity = Array.from(accountLimits.values()).reduce((sum, v) => sum + v, 0);

      days.push({
        date: currentDay,
        jobs: dayJobs,
        capacity,
        sent: sentCountByDay.get(currentDay.toISODate()) ?? 0,
        isCurrentMonth: currentDay.hasSame(currentDate, 'month'),
        isToday: currentDay.hasSame(DateTime.now(), 'day')
      });

      currentDay = currentDay.plus({ days: 1 });
    }

    return days;
  }, [currentDate, filteredJobs]);

  // Roll up visible capacity + account count across all filtered days for the legend
  const { totalVisibleCapacity, visibleAccountCount } = useMemo(() => {
    const accountLimits = new Map<string, number>();
    for (const day of calendarDays) {
      for (const job of day.jobs) {
        if (!accountLimits.has(job.provider_email)) {
          accountLimits.set(job.provider_email, job.daily_limit);
        }
      }
    }
    let totalVisibleCapacity = 0;
    for (const v of accountLimits.values()) totalVisibleCapacity += v;
    return { totalVisibleCapacity, visibleAccountCount: accountLimits.size };
  }, [calendarDays]);

  const cancelJob = async (jobId: string) => {
    try {
      const { error } = await supabase
        .from('hermes_email_queue_jobs')
        .update({ status: 'cancelled' })
        .eq('id', jobId);

      if (error) throw error;

      toast({
        description: "Email job cancelled successfully",
      });
    } catch (error) {
      console.error('Error cancelling job:', error);
      captureError(error as Error, { feature: 'email-scheduled-emails-calendar-cancel-job' });
      toast({
        title: "Error",
        description: "Failed to cancel email job",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-4">
          <h2 className="text-2xl font-semibold tracking-tight">
            {currentDate.toFormat('MMMM yyyy')}
          </h2>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="icon"
              onClick={() => setCurrentDate(prev => prev.minus({ months: 1 }))}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="icon"
              onClick={() => setCurrentDate(prev => prev.plus({ months: 1 }))}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Select
            value={selectedProviders.join(',')}
            onValueChange={(value) => setSelectedProviders(value === 'all' ? [] : value.split(','))}
          >
            <SelectTrigger className="w-[250px]">
              <SelectValue placeholder="Filter by provider" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Providers</SelectItem>
              {providers.map(provider => (
                <SelectItem key={provider} value={provider}>
                  {provider}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select
            value={selectedCampaigns.join(',')}
            onValueChange={(value) => setSelectedCampaigns(value === 'all' ? [] : value.split(','))}
          >
            <SelectTrigger className="w-[250px]">
              <SelectValue placeholder="Filter by campaign" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Campaigns</SelectItem>
              {campaigns.map(campaign => (
                <SelectItem key={campaign.id} value={campaign.id}>
                  {campaign.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-muted-foreground">
        <div className="flex items-center gap-3">
          <span className="flex items-center gap-1.5">
            <span className="inline-block h-2.5 w-2.5 rounded-full bg-emerald-500" aria-hidden />
            Delivered
          </span>
          <span className="flex items-center gap-1.5">
            <span className="inline-block h-2.5 w-2.5 rounded-full border border-amber-400/70 bg-amber-400/30" aria-hidden />
            Queued to send
          </span>
        </div>
        {visibleAccountCount > 0 && (
          <span>Capacity {totalVisibleCapacity}/day · {visibleAccountCount} accounts</span>
        )}
      </div>
      {overdueCount > 0 && (
        <div className="text-xs text-amber-600">
          {overdueCount} queued email{overdueCount === 1 ? '' : 's'} {overdueCount === 1 ? 'is' : 'are'} behind schedule —
          they weren't sent on their scheduled day and roll into the next available slot (the daily limit per account).
        </div>
      )}

      <div className="grid grid-cols-7 gap-px bg-muted rounded-lg overflow-hidden">
        {/* Weekday headers */}
        {WEEKDAYS.map(day => (
          <div key={day} className="bg-background p-2 text-center text-sm font-medium">
            {day}
          </div>
        ))}

        {/* Calendar days */}
        {calendarDays.map((day) => (
          <button
            key={day.date.toISO()}
            className={cn(
              "min-h-[120px] p-2 bg-background text-left transition-colors",
              "hover:bg-accent/50 focus-visible:bg-accent/50 focus-visible:outline-none",
              day.isCurrentMonth ? "text-foreground" : "text-muted-foreground",
              day.isToday && "ring-2 ring-primary",
              selectedDate.hasSame(day.date, 'day') && "bg-accent"
            )}
            onClick={() => setSelectedDate(day.date)}
          >
            <div className="font-medium text-sm">
              {day.date.toFormat('d')}
            </div>
            <div className="mt-1 space-y-0.5">
              {day.sent > 0 && (
                <Badge
                  variant="secondary"
                  title={`${day.sent} delivered on ${day.date.toFormat('MMM d')}`}
                  className="text-xs bg-emerald-500/15 text-emerald-700 dark:text-emerald-300"
                >
                  {day.sent} sent
                </Badge>
              )}
              {day.isToday && todayEligible > 0 && (
                <Badge
                  variant="outline"
                  title={`${todayEligible} queued to send today — includes behind-schedule emails that roll forward`}
                  className="text-xs border-amber-400/60 text-amber-700 dark:text-amber-300"
                >
                  {todayEligible} queued
                </Badge>
              )}
              {!day.isToday && (day.date.toISODate() ?? '') > (startOfToday.toISODate() ?? '') && day.jobs.length > 0 && (
                <Badge
                  variant="outline"
                  title={`${day.jobs.length} queued; ${visibleAccountCount || '?'} account(s) at ~${(totalVisibleCapacity || '?')}/day`}
                  className="text-xs border-amber-400/60 text-amber-700 dark:text-amber-300"
                >
                  {day.jobs.length} queued
                </Badge>
              )}
            </div>
          </button>
        ))}
      </div>

      {/* Selected day details */}
      {selectedDate && (
        <Card>
          <div className="p-4 border-b">
            <h3 className="font-medium">
              {selectedDate.toLocaleString(DateTime.DATE_FULL)}
            </h3>
          </div>
          <ScrollArea className="h-[300px]">
            <div className="p-4 space-y-4">
              {calendarDays
                .find(day => day.date.hasSame(selectedDate, 'day'))
                ?.jobs.map(job => (
                  <div
                    key={job.id}
                    className="flex items-center justify-between p-3 rounded-lg border bg-card"
                  >
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <Badge 
                          variant="secondary"
                          className={cn(
                            "capitalize",
                            getProviderColor(job.provider_email)
                          )}
                        >
                          {job.provider_email}
                        </Badge>
                        <span className="text-sm text-muted-foreground">
                          {DateTime.fromISO(job.scheduled_send_time).toLocaleString(DateTime.TIME_SIMPLE)}
                        </span>
                      </div>
                      <div className="font-medium">{job.rendered_subject}</div>
                      <div className="text-sm text-muted-foreground">
                        To: {job.recipient_email}
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => cancelJob(job.id)}
                      className="text-destructive"
                    >
                      <XCircle className="h-4 w-4 mr-1" />
                      Cancel
                    </Button>
                  </div>
                ))}

              {(!calendarDays.find(day => day.date.hasSame(selectedDate, 'day'))?.jobs.length) && (
                <div className="text-center py-8 text-muted-foreground">
                  No scheduled emails for this day
                </div>
              )}
            </div>
          </ScrollArea>
        </Card>
      )}
    </div>
  );
}